import React from "react";
import { useState } from "react";
import { motion } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { NavLink } from "react-router-dom";

import useAnimatedNavToggler from "../../helpers/useAnimatedNavToggler.js";

import logo from "../../images/logo.svg";
import { ReactComponent as MenuIcon } from "feather-icons/dist/icons/menu.svg";
import { ReactComponent as CloseIcon } from "feather-icons/dist/icons/x.svg";
import { ReactComponent as ChevronDownIcon } from "feather-icons/dist/icons/chevron-down.svg";

const Header = tw.header`
  flex justify-between items-center
  max-w-screen-xl mx-auto
`;

export const NavLinks = tw.div`inline-block`;

/* hocus: stands for "on hover or focus"
 * hocus:bg-primary-700 will apply the bg-primary-700 class on hover or focus
 */
export const NavLinkStyled = styled(NavLink)`
  ${tw `
    text-lg my-2 lg:text-sm lg:mx-6 lg:my-0
    font-semibold tracking-wide transition duration-300
    pb-1 border-b-2 border-transparent hover:border-primary-500 hocus:text-primary-500
  `}
  &.active {
    ${tw`text-primary-500 border-b-2 border-primary-500`}
  }

`;

export const LogoLink = styled(NavLinkStyled)`
  ${tw`flex items-center font-black border-b-0 text-2xl! ml-0!`};

  img {
    ${tw`w-10 mr-3`}
  }
`;

export const NavLinkExpandable = styled.button`
  ${tw `
    text-lg my-2 lg:text-sm lg:mx-6 lg:my-0
    font-semibold tracking-wide transition duration-300
    pb-1 border-b-2 border-transparent hover:border-primary-500 hocus:text-primary-500
  `}
  &.active {
    ${tw`text-primary-500 border-b-2 border-primary-500`}
  }
`;

export const NavLinkMenu = styled.ul`
  ${tw `
    lg:absolute z-10 mt-2 w-56 
    origin-top-right rounded-md bg-white shadow-lg 
    ring-1 ring-black ring-opacity-5 focus:outline-none 

  `}

  a {
    ${tw `inline-block my-2`}
  }
`;

const ExpandableMenu = tw.div`
  relative inline-block
`

export const MobileNavLinksContainer = tw.nav`flex flex-1 items-center justify-between`;
export const NavToggle = tw.button`
  lg:hidden z-20 focus:outline-none hocus:text-primary-500 transition duration-300
`;
export const MobileNavLinks = motion(styled.div`
  ${tw`lg:hidden z-10 fixed top-0 inset-x-0 mx-4 my-6 p-8 border text-center rounded-lg text-gray-900 bg-white`}
  ${NavLinks} {
    ${tw`flex flex-col items-center`}
  }
`);

export const DesktopNavLinks = tw.nav`
  hidden lg:flex flex-1 justify-between items-center
`;

export default ({ a14n = null, roundedHeaderButton = false, logoLink, links, className, collapseBreakpointClass = "lg" }) => {
  /*
   * This header component accepts an optionals "links" prop that specifies the links to render in the navbar.
   * This links props should be an array of "NavLinks" components which is exported from this file.
   * Each "NavLinks" component can contain any amount of "NavLink" component, also exported from this file.
   * This allows this Header to be multi column.
   * So If you pass only a single item in the array with only one NavLinks component as root, you will get 2 column header.
   * Left part will be LogoLink, and the right part will be the the NavLinks component you
   * supplied.
   * Similarly if you pass 2 items in the links array, then you will get 3 columns, the left will be "LogoLink", the center will be the first "NavLinks" component in the array and the right will be the second "NavLinks" component in the links array.
   * You can also choose to directly modify the links here by not passing any links from the parent component and
   * changing the defaultLinks variable below below.
   * If you manipulate links here, all the styling on the links is already done for you. If you pass links yourself though, you are responsible for styling the links or use the helper styled components that are defined here (NavLink)
   */
  const [menuShow, setMenuShow] = useState({tickets: false, reports: false});

  function toggleMenu(type) {
    setMenuShow((prevState) => {
      const newMenu = {}
      newMenu[type] = !prevState[type];
      for (var key in prevState) {
        if (key !== type) prevState[key] = false;
      }
      return {...prevState, ...newMenu}
    });
  }

  const month = (new Date()).toLocaleString(navigator.languages[0], {month: 'long'});

  const defaultLinks = [
    <NavLinks key={1}>
      <NavLinkStyled activeClassName="active" to="/">Вернуться</NavLinkStyled>
      <ExpandableMenu>
        <NavLinkExpandable onClick={toggleMenu.bind(this, 'reports')}>Отчёты <ChevronDownIcon tw="w-6 h-6 inline-block" /></NavLinkExpandable>
        {
          menuShow.reports && 
          <NavLinkMenu>
            <li><NavLinkStyled activeClassName="active" to="/admin/services/my">Мой за сегодня</NavLinkStyled></li>
            <li><NavLinkStyled activeClassName="active" to={`/admin/reportmy`}>Мой за {month}</NavLinkStyled></li>
            {
              a14n && a14n.is_admin && 
                <>
                  <li><NavLinkStyled activeClassName="active" to="/admin/report">Ежедневный за {month}</NavLinkStyled></li>
                  <li><NavLinkStyled activeClassName="active" to="/admin/log">Журнал</NavLinkStyled></li>
                  <li><NavLinkStyled activeClassName="active" to="/admin/report_income">Прибыль и затраты</NavLinkStyled></li>
                </>
            }
          </NavLinkMenu>
        }
      </ExpandableMenu>

      <ExpandableMenu>
        <NavLinkExpandable onClick={toggleMenu.bind(this, 'gym')}>Скалодром <ChevronDownIcon tw="w-6 h-6 inline-block" /></NavLinkExpandable>
          {menuShow.gym && 
            <NavLinkMenu>
              <li><NavLinkStyled activeClassName="active" to="/admin/groups/index">Группы</NavLinkStyled></li>
              {
                a14n && a14n.is_admin && 
                <>
                  <li><NavLinkStyled activeClassName="active" to="/admin/groups/add">Создать группу</NavLinkStyled></li>
                  <li><NavLinkStyled activeClassName="active" to="/admin/discounts">Скидки</NavLinkStyled></li>
                </> 
              }
              <li><NavLinkStyled activeClassName="active" to="/admin/services/index">Все услуги</NavLinkStyled></li>
            </NavLinkMenu>
          }
      </ExpandableMenu>


      

      <ExpandableMenu>
        <NavLinkExpandable onClick={toggleMenu.bind(this, 'tickets')}>Абонементы <ChevronDownIcon tw="w-6 h-6 inline-block" /></NavLinkExpandable>
          {menuShow.tickets && 
            <NavLinkMenu>
              <li><NavLinkStyled activeClassName="active" to="/admin/tickets">Активированные</NavLinkStyled></li>
              {
                a14n && a14n.is_admin && 
                <>
                  <li><NavLinkStyled activeClassName="active" to="/admin/expiring">Истекающие</NavLinkStyled></li>
                  <li><NavLinkStyled to="/admin/tickets_inactive">Незанятые</NavLinkStyled></li>
                </> 
              }
            </NavLinkMenu>
          }
      </ExpandableMenu>
      <NavLinkStyled to="/signout">Выход</NavLinkStyled>
    </NavLinks>
  ];

  const { showNavLinks, animation, toggleNavbar } = useAnimatedNavToggler();
  const collapseBreakpointCss = collapseBreakPointCssMap[collapseBreakpointClass];

  const defaultLogoLink = (
    <LogoLink to="/admin/services/my">
      <img style={{width: 60}} src={logo} alt="logo" />
      Админка
    </LogoLink>
  );

  logoLink = logoLink || defaultLogoLink;
  links = links || defaultLinks;

  return (
    <Header className={className || "header-light"}>
      <DesktopNavLinks css={collapseBreakpointCss.desktopNavLinks}>
        {logoLink}
        {links}
      </DesktopNavLinks>

      <MobileNavLinksContainer css={collapseBreakpointCss.mobileNavLinksContainer}>
        {logoLink}
        <MobileNavLinks initial={{ x: "150%", display: "none" }} animate={animation} css={collapseBreakpointCss.mobileNavLinks}>
          {links}
        </MobileNavLinks>
        <NavToggle onClick={toggleNavbar} className={showNavLinks ? "open" : "closed"}>
          {showNavLinks ? <CloseIcon tw="w-6 h-6" /> : <MenuIcon tw="w-6 h-6" />}
        </NavToggle>
      </MobileNavLinksContainer>
    </Header>
  );
};

/* The below code is for generating dynamic break points for navbar.
 * Using this you can specify if you want to switch
 * to the toggleable mobile navbar at "sm", "md" or "lg" or "xl" above using the collapseBreakpointClass prop
 * Its written like this because we are using macros and we can not insert dynamic variables in macros
 */

const collapseBreakPointCssMap = {
  sm: {
    mobileNavLinks: tw`sm:hidden`,
    desktopNavLinks: tw`sm:flex`,
    mobileNavLinksContainer: tw`sm:hidden`
  },
  md: {
    mobileNavLinks: tw`md:hidden`,
    desktopNavLinks: tw`md:flex`,
    mobileNavLinksContainer: tw`md:hidden`
  },
  lg: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`
  },
  xl: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`
  }
};
